import { Component } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IFloatingFilterAngularComp } from 'ag-grid-angular';
import { IFloatingFilterParams } from 'ag-grid-community';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';
import { TableColumn } from '../interfaces';

/** What the floating filter needs from the hosting table component */
export interface FloatingFilterHost {
    filterForm: FormGroup;
    compareSearchOption: (a: unknown, b: unknown) => boolean;
}

export interface TableFloatingFilterParams extends IFloatingFilterParams {
    tableColumn: TableColumn;
    context: FloatingFilterHost;
}

/**
 * The typed per-column search control rendered in AG Grid's floating filter
 * row, bound to the host table's FormGroup. The markup mirrors the
 * static-table search row (search icon, clear buttons, calendar toggle) so
 * the two tables look and behave the same. The grid's own filter model is
 * intentionally unused: filtering is external.
 */
@Component({
    selector: 'table-floating-filter',
    template: `
        <ng-container *ngIf="control != null" [ngSwitch]="col.searchable">
            <div *ngSwitchCase="'text'" class="inner-addon left-addon">
                <span class="fa fa-search"></span>
                <input
                    type="text"
                    class="form-control input-sm"
                    [class.input-clear-active]="hasValue"
                    [formControl]="control"
                    [placeholder]="col.searchPlaceholder ?? ''"
                    data-cy="tableTextInput"
                />
                <span
                    *ngIf="hasValue"
                    class="input-group-clear text"
                    (click)="clear()"
                >
                    <i class="fal fa-times"></i>
                </span>
            </div>
            <div *ngSwitchCase="'date'" class="input-group input-group-sm">
                <input
                    class="form-control"
                    [class.input-clear-active]="hasValue"
                    #datePicker="bsDatepicker"
                    bsDatepicker
                    [formControl]="control"
                    [placeholder]="col.searchPlaceholder ?? ''"
                />
                <span
                    *ngIf="hasValue"
                    class="input-group-addon input-group-clear calendar"
                    (click)="clear()"
                >
                    <i class="fal fa-times"></i>
                </span>
                <span
                    class="input-group-addon btn-default"
                    (click)="datePicker.toggle()"
                    [attr.aria-expanded]="datePicker.isOpen"
                >
                    <i class="glyphicon glyphicon-calendar"></i>
                </span>
            </div>
            <div *ngSwitchCase="'daterange'" class="input-group input-group-sm">
                <input
                    class="form-control"
                    [class.input-clear-active]="hasValue"
                    #datePicker="bsDaterangepicker"
                    bsDaterangepicker
                    [bsConfig]="dateRangePickerConfig"
                    placement="bottom"
                    [container]="'body'"
                    [formControl]="control"
                    [placeholder]="col.searchPlaceholder ?? ''"
                />
                <span
                    *ngIf="hasValue"
                    class="input-group-addon input-group-clear calendar"
                    (click)="clear()"
                >
                    <i class="fal fa-times"></i>
                </span>
                <span
                    class="input-group-addon btn-default"
                    (click)="datePicker.toggle()"
                    [attr.aria-expanded]="datePicker.isOpen"
                >
                    <i class="glyphicon glyphicon-calendar"></i>
                </span>
            </div>
            <table-async-selection
                *ngSwitchCase="'async-selection'"
                [columnFormControl]="control"
                [graphQlService]="col.asyncSelectService"
                [clearable]="
                    col.clearableSearch != null ? col.clearableSearch : true
                "
                [placeholder]="
                    col.searchPlaceholder ? col.searchPlaceholder : 'Search'
                "
            ></table-async-selection>
            <ng-select
                *ngSwitchCase="'selection'"
                class="wide-panel"
                [items]="col.searchOptions"
                bindLabel="name"
                bindValue="value"
                [groupBy]="col.groupBy"
                [compareWith]="compareOption"
                [formControl]="control"
                appendTo="body"
                [clearable]="
                    col.clearableSearch != null ? col.clearableSearch : true
                "
                [placeholder]="
                    col.searchPlaceholder ? col.searchPlaceholder : 'Search'
                "
            ></ng-select>
        </ng-container>
    `,
    styles: [
        `
            :host {
                display: block;
                width: 100%;
            }
            /* The static-table search icon positioning, scoped there to
               .static-table-container, reproduced for the floating filter */
            .inner-addon {
                position: relative;
            }
            .inner-addon .fa {
                position: absolute;
                padding: 7px;
                pointer-events: none;
            }
            .left-addon .fa {
                left: 0;
            }
            .left-addon input {
                padding-left: 26px;
            }
            /* Bootstrap's small controls (30px) as in the static-table
               search row; the header font weight must not leak in */
            .form-control,
            .input-group-addon {
                font-weight: normal;
            }
            ::ng-deep .wide-panel .ng-select-container {
                min-height: 30px;
                height: 30px;
                font-size: 12px;
                font-weight: normal;
            }
        `
    ]
})
export class TableFloatingFilter implements IFloatingFilterAngularComp {
    col: TableColumn;
    host: FloatingFilterHost;
    control: FormControl;
    dateRangePickerConfig: Partial<BsDatepickerConfig>;

    /**
     * ng-select hands compareWith the whole option (`{ name, value }`) and
     * the bound value, so compare the option's value; otherwise a written
     * value is never matched and shows as an empty selection.
     */
    compareOption = (option: { value?: unknown }, value: unknown) =>
        this.host.compareSearchOption(option?.value, value);

    constructor(bsDatepickerConfig: BsDatepickerConfig) {
        this.dateRangePickerConfig = {
            adaptivePosition: false,
            dateInputFormat: bsDatepickerConfig.dateInputFormat,
            rangeInputFormat: bsDatepickerConfig.rangeInputFormat
        };
    }

    agInit(params: TableFloatingFilterParams) {
        this.col = params.tableColumn;
        this.host = params.context;
        // controls[key], not get(key): keys may contain dots (see DataTable.filterForm)
        this.control = this.host.filterForm.controls[
            this.col.key
        ] as FormControl;
    }

    /**
     * The grid keeps this instance when the column definition changes (e.g.
     * searchOptions that arrive after the first render) and only hands the
     * new params to `refresh`, so take them exactly as on init.
     */
    refresh(params: TableFloatingFilterParams) {
        this.agInit(params);
    }

    onParentModelChanged() {
        // Filtering is external; the grid's filter model is not used
    }

    get hasValue(): boolean {
        const value = this.control?.value;
        if (value == null) {
            return false;
        }
        // A single date is a Date object; text and ranges have a length
        return value instanceof Date || value.length > 0;
    }

    clear() {
        this.control.patchValue(undefined);
    }
}
